import { QueryClient, useQueryClient } from "@tanstack/react-query"
import eq from "lodash-es/eq"
import every from "lodash-es/every"
import isEmpty from "lodash-es/isEmpty"
import isNumber from "lodash-es/isNumber"
import { useEffectOnce } from "react-use"

import { reduxBatch } from "@/3514/utils"

import { ECoachingChatQueryKey as EQueryKey } from "../config"
import {
    useCoachingChatConversationDispatcher as useConversationDispatcher,
    useCoachingChatFormDispatcher as useFormDispatcher
} from "../store"
import {
    ECoachingChatConversationStatus as EConversationStatus,
    type ICoachingChatConversationQueryDto,
    type TCoachingChatConfig as TChatConfig,
    type TCoachingChatSeedQuestion as TSeedQuestion,
    type _TCoachingChatHookSharedParams as _TSharedParams
} from "../types"
import { coachingChatUiUtils as uiUtils } from "../utils"

const {
    formExtractors: { mapSeedQuestionsToFormFields }
} = uiUtils

type _TParams = Pick<_TSharedParams, "entityId" | "wasChatCompleted"> &
    Pick<TChatConfig, "conversationId" | "seedQuestions" | "isChatAvailable">

function useCoachingChatInitialLoader({
    entityId,
    wasChatCompleted,
    conversationId: existingConversationId,
    seedQuestions,
    isChatAvailable
}: _TParams): void {
    const qc: QueryClient = useQueryClient()

    const formDispatcher: ReturnType<typeof useFormDispatcher> = useFormDispatcher(entityId)

    const conversationDispatcher: ReturnType<typeof useConversationDispatcher> = useConversationDispatcher(entityId)

    useEffectOnce((): void => {
        const _wasCreated: boolean = isNumber(existingConversationId)
        const _wereSeedsDone: boolean = every<TSeedQuestion>(
            seedQuestions,
            (s: TSeedQuestion): boolean => !isEmpty(s.answer)
        )

        reduxBatch((): void => {
            if (!isEmpty(seedQuestions)) formDispatcher.loadSeeds(mapSeedQuestionsToFormFields(seedQuestions))

            if (wasChatCompleted && isChatAvailable) {
                formDispatcher.submitForm()
                conversationDispatcher.setConversationId(existingConversationId)
                conversationDispatcher.changeStatus(EConversationStatus.Completed)
            } else if (!wasChatCompleted && isChatAvailable) {
                if (!_wasCreated && !_wereSeedsDone) conversationDispatcher.changeStatus(EConversationStatus.Locked)

                if (_wasCreated || _wereSeedsDone) {
                    if (!_wasCreated && _wereSeedsDone)
                        conversationDispatcher.changeStatus(EConversationStatus.ReadyToStart)

                    if (_wasCreated && _wereSeedsDone) {
                        conversationDispatcher.setConversationId(existingConversationId)
                        conversationDispatcher.changeStatus(EConversationStatus.Ready)
                        formDispatcher.submitForm()
                    }
                }

                if (isChatAvailable) {
                    const _chatCachedData: ICoachingChatConversationQueryDto = qc.getQueryData(
                        EQueryKey.Conversation(existingConversationId)
                    )

                    if (!isEmpty(_chatCachedData)) {
                        if (eq(_chatCachedData.status, "in_progress"))
                            conversationDispatcher.changeStatus(EConversationStatus.Ready)

                        if (eq(_chatCachedData.status, "completed"))
                            conversationDispatcher.changeStatus(EConversationStatus.Completed)
                    }
                }
            } else if (!isChatAvailable) if (_wasCreated) formDispatcher.submitForm()
        })
    })
}

export { useCoachingChatInitialLoader }
